import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../../lib/supabase";

type HairTypeItem = {
  id: string;
  name: string;
  slug: string;
  group_id: string;
};

type HairTypeMenuProps = {
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  variant?: "desktop" | "mobile";
  onBack?: () => void;
  groupSlug?: string;
  title?: string;
  productParam?: string;
};

const ShopByHairTypePage: React.FC<HairTypeMenuProps> = ({
  isOpen,
  variant = "desktop",
  onBack,
  onClose,
  groupSlug = "hair-type",
  title = "Shop by Hair Type",
  productParam = "hairType",
}) => {
  const navigate = useNavigate();
  const [hairTypes, setHairTypes] = useState<HairTypeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchHairTypes = async () => {
      setLoading(true);
      setErrorMessage(null);

      const { data: groupRow, error: groupError } = await supabase
        .from("attribute_groups")
        .select("id, name, slug")
        .eq("slug", groupSlug)
        .single();

      console.log("hair type group:", groupRow, groupError);

      if (groupError || !groupRow) {
        setHairTypes([]);
        setErrorMessage(groupError?.message || `Could not find ${title} group.`);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("attributes")
        .select("id, name, slug, group_id")
        .eq("group_id", groupRow.id)
        .order("name", { ascending: true });

      console.log("hair type values:", data, error);

      if (error) {
        setHairTypes([]);
        setErrorMessage(error.message);
      } else {
        setHairTypes(data || []);
      }

      setLoading(false);
    };

    if (isOpen) {
      fetchHairTypes();
    }
  }, [groupSlug, isOpen, title]);

  const handleSelect = (slug: string) => {
    navigate(`/products?${productParam}=${slug}`);
    onClose();
  };

  if (!isOpen) return null;

  if (variant === "mobile") {
    return (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-3 py-3 rounded-xl text-[#5c2f2f] font-semibold hover:bg-[#f7dada]/50 transition"
        >
          <ChevronLeft size={18} />
          Back
        </button>

        {loading ? (
          <div className="px-3 py-3 text-sm text-[#7a4343]">
            Loading hair types...
          </div>
        ) : errorMessage ? (
          <div className="px-3 py-3 text-sm text-red-500 break-words">
            {errorMessage}
          </div>
        ) : hairTypes.length === 0 ? (
          <div className="px-3 py-3 text-sm text-[#7a4343]">
            No hair types found.
          </div>
        ) : (
          hairTypes.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item.slug)}
              className="w-full text-left px-4 py-3 rounded-2xl bg-white/70 hover:bg-[#fdf1f1] transition border border-[#f1d6d6] flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-[#5c2f2f] font-semibold text-[15px] leading-5 break-words">
                  {item.name}
                </div>
              </div>

              <ChevronRight size={16} className="shrink-0 mt-1 text-[#7a4343]" />
            </button>
          ))
        )}
      </div>
    );
  }

  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[360px] max-w-[92vw] rounded-3xl bg-white/95 backdrop-blur-xl border border-white/70 shadow-[0_18px_55px_rgba(0,0,0,0.18)] p-4 z-50">
      <div className="mb-3 px-1">
        <div className="text-[#5c2f2f] font-bold text-sm tracking-wide">
          {title}
        </div>
      </div>

      {loading ? (
        <div className="px-2 py-3 text-sm text-[#7a4343]">
          Loading hair types...
        </div>
      ) : errorMessage ? (
        <div className="px-2 py-3 text-sm text-red-500 break-words">
          {errorMessage}
        </div>
      ) : hairTypes.length === 0 ? (
        <div className="px-2 py-3 text-sm text-[#7a4343]">
          No hair types found.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          {hairTypes.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item.slug)}
              className="w-full text-left px-4 py-3 rounded-2xl bg-[#fffafa] hover:bg-[#fdf1f1] transition border border-[#f1d6d6] flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-[#5c2f2f] font-semibold text-[15px] leading-5 break-words">
                  {item.name}
                </div>
              </div>

              <ChevronRight size={16} className="shrink-0 mt-1 text-[#7a4343]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopByHairTypePage;
